import { t } from 'i18next'
import { IMooringType, IPositionType } from 'src/generated-types'
import { ISelectedPositionTypes } from 'src/generated-types/position-request-params'
import { useEffect } from 'react'
import { GridApiPro } from '@mui/x-data-grid-pro/models/gridApiPro'

interface ColumnWidths {
  widths: number[]
  leftOffset: number
}

export const positionTypesArray: IPositionType[] = [
  IPositionType.MooringLine,
  IPositionType.Corner,
  IPositionType.Bridle,
  IPositionType.FrameLines,
  IPositionType.Cage,
]

export const getKey = (type: IPositionType) => {
  switch (type) {
    case IPositionType.MooringLine:
      return 'mooringLines'
    case IPositionType.Corner:
      return 'corners'
    case IPositionType.Bridle:
      return 'bridles'
    case IPositionType.FrameLines:
      return 'frameLines'
    case IPositionType.Cage:
      return 'cages'
    default:
      return 'cages'
  }
}

export const buildPositionFilterParams = (
  positionTypeFilter: ISelectedPositionTypes,
  isCritical: boolean | null,
): ISelectedPositionTypes => ({
  ...positionTypeFilter,
  allPositions: !(
    positionTypeFilter.mooringLines ||
    positionTypeFilter.frameLines ||
    positionTypeFilter.corners ||
    positionTypeFilter.bridles ||
    positionTypeFilter.cages ||
    positionTypeFilter.drawnGeometries
  ),
  isCritical: isCritical,
})

export const getMooringName = (type: IMooringType) => {
  return t(IMooringType[type], { ns: 'mooring' })
}

export const getHeaderKeyList = (
  t,
): Array<{ headerName: string; flex: number; width?: number; marginLeft?: number }> => [
  { headerName: t('position', { ns: 'position' }), flex: 1 },
  { headerName: t('reference', { ns: 'position' }), flex: 1 },
  { headerName: t('latitude', { ns: 'common' }) + '/' + t('longitude', { ns: 'common' }), flex: 1.6 },
  { headerName: t('mbl', { ns: 'position' }), flex: 1 },
  { headerName: t('accidentLoad', { ns: 'position' }), flex: 1.1 },
  { headerName: t('depth', { ns: 'position' }), flex: 1 },
  { headerName: t('direction', { ns: 'position' }), flex: 1 },
  { headerName: t('testLoad', { ns: 'position' }), flex: 1.1 },
  { headerName: t('bottomAttachment', { ns: 'position' }), flex: 1.6 },
  { headerName: t('sediment', { ns: 'position' }), flex: 1.8 },
]

const STATIC_FIELDS = [
  'name',
  'reference',
  'latitude',
  'mbl',
  'accidentLoad',
  'depth',
  'direction',
  'testLoad',
  'bottomFasten',
  'sediment',
] as const

export const useColumnWidthMeasurement = (
  apiRef: React.MutableRefObject<GridApiPro>,
  positionsLength: number,
  checkboxSelection: boolean,
  onColumnWidthsChange?: (widths: ColumnWidths) => void,
) => {
  useEffect(() => {
    if (!onColumnWidthsChange || !apiRef.current || positionsLength === 0) {
      return
    }

    const measureWidths = () => {
      try {
        const allColumns = apiRef.current.getAllColumns()

        if (!allColumns.some(col => col.computedWidth > 0)) {
          return
        }

        const visibleColumns = allColumns.filter(
          col => col.field !== '__detail_panel_toggle__' && col.field !== '__check__',
        )

        const widths = STATIC_FIELDS.map(field => {
          const column = visibleColumns.find(col => col.field === field)
          return column?.computedWidth || 0
        })

        let leftOffset = 0
        const detailPanelCol = allColumns.find(col => col.field === '__detail_panel_toggle__')
        if (detailPanelCol) {
          leftOffset += detailPanelCol.computedWidth || 0
        }
        if (checkboxSelection) {
          const checkboxCol = allColumns.find(col => col.field === 'id')
          if (checkboxCol) {
            leftOffset += checkboxCol.computedWidth || 0
          }
        }

        onColumnWidthsChange({ widths, leftOffset })
      } catch (error) {
        void error
      }
    }

    // Subscribe to grid events that affect column widths
    const unsubscribeResize = apiRef.current.subscribeEvent('viewportInnerSizeChange', measureWidths)
    const unsubscribeColumnResize = apiRef.current.subscribeEvent('columnWidthChange', measureWidths)
    const unsubscribeStateChange = apiRef.current.subscribeEvent('stateChange', measureWidths)

    // Initial measurement using requestAnimationFrame to ensure DOM is ready
    requestAnimationFrame(() => {
      requestAnimationFrame(measureWidths)
    })

    return () => {
      unsubscribeResize()
      unsubscribeColumnResize()
      unsubscribeStateChange()
    }
  }, [apiRef, onColumnWidthsChange, positionsLength, checkboxSelection])
}
